import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Alert } from 'react-bootstrap';
import { ShoppingBag, Package, Users, BarChart2, AlertTriangle, Calendar, DollarSign, ClipboardList, TrendingUp } from 'lucide-react';
import useAuth from '../hooks/useAuth';
import { getAllProducts } from '../api/products';
import { getAllOrders, getOrdersByPartner } from '../api/orders';
import Loading from '../components/common/Loading';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Dashboard = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        // Stats communs
        totalProducts: 0,
        ordersThisMonth: 0,

        // Stats admin/manager
        activeClients: 0,
        lowStockProducts: [],
        salesData: [],

        // Stats distributeur
        totalOrders: 0,
        pendingOrders: 0,
        mostOrderedProducts: [],
        recentOrders: [],
        ordersByStatus: [],
        ordersByMonth: []
    });

    
    // Couleurs pour les graphiques
    const STATUS_COLORS = {
        'PENDING': '#f0ad4e',     // orange/warning
        'CONFIRMED': '#0d6efd',   // blue/primary
        'DELIVERED': '#198754',   // green/success
        'REJECTED': '#dc3545'     // red/danger
    };

    // Mapping des statuts pour l'affichage
    const statusMapping = {
        'PENDING': 'En attente',
        'CONFIRMED': 'Confirmée',
        'DELIVERED': 'Livrée',
        'REJECTED': 'Rejetée'
    };

    // Fonction sécurisée pour obtenir le nom du client à partir d'une commande
    const getClientName = (order) => {
        if (order?.partner?.name) {
            return order.partner.name;
        }
        if (order?.client?.name) {
            return order.client.name;
        }
        return "Client non spécifié";
    };

    // Helper function to format relative time
    const getRelativeTime = (date) => {
        const now = new Date();
        const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

        if (diffInHours < 1) {
            return "Il y a moins d'une heure";
        } else if (diffInHours < 24) {
            return `Il y a ${diffInHours} heure${diffInHours > 1 ? 's' : ''}`;
        } else if (diffInHours < 48) {
            return "Hier";
        } else {
            return `Il y a ${Math.floor(diffInHours / 24)} jours`;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount, withSymbol = false) => {
        // Format avec un point comme séparateur de milliers et virgule pour décimales
        if (!amount) return "0 FCFA";
        const parts = Number(amount).toFixed().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const formattedValue = parts.join(',');

        // Ajout du symbole de devise si demandé
        return withSymbol ? formattedValue + ' FCFA' : formattedValue;
    };

    // Fonction pour obtenir le badge de statut
    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning">En attente</Badge>;
            case 'CONFIRMED':
                return <Badge bg="primary">Confirmée</Badge>;
            case 'DELIVERED':
                return <Badge bg="success">Livrée</Badge>;
            case 'REJECTED':
                return <Badge bg="danger">Rejetée</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    // Fonction pour extraire les produits les plus commandés
    const getMostOrderedProducts = (orders, limit = 5) => {
        // Collecter tous les produits commandés avec leurs quantités
        const productMap = new Map();

        orders.forEach(order => {
            // Ne compter que les commandes confirmées et livrées
            if (['CONFIRMED', 'DELIVERED'].includes(order.status)) {
                if (order.items && Array.isArray(order.items)) {
                    order.items.forEach(item => {
                        if (item.product) {
                            const productId = item.product.id;
                            const currentQuantity = productMap.get(productId)?.quantity || 0;
                            const currentOrders = productMap.get(productId)?.orderCount || 0;

                            productMap.set(productId, {
                                product: item.product,
                                quantity: currentQuantity + item.quantity,
                                orderCount: currentOrders + 1,
                                lastOrderDate: new Date(order.orderDate) > (productMap.get(productId)?.lastOrderDate || new Date(0))
                                    ? new Date(order.orderDate)
                                    : productMap.get(productId)?.lastOrderDate
                            });
                        }
                    });
                }
            }
        });

        // Trier par quantité totale
        return Array.from(productMap.values())
            .sort((a, b) => b.quantity - a.quantity)
            .slice(0, limit);
    };

    // Fonction pour obtenir les commandes par statut
    const getOrdersByStatus = (orders) => {
        const statusCount = {
            PENDING: 0,
            CONFIRMED: 0,
            DELIVERED: 0,
            REJECTED: 0
        };

        orders.forEach(order => {
            if (statusCount[order.status] !== undefined) {
                statusCount[order.status]++;
            }
        });

        return Object.entries(statusCount)
            .map(([status, count]) => ({
                name: statusMapping[status] || status,
                value: count,
                color: STATUS_COLORS[status]
            }))
            .filter(item => item.value > 0);
    };

    // Fonction pour obtenir les commandes par mois
    const getOrdersByMonth = (orders, months = 6) => {
        const today = new Date();
        const result = [];

        // Générer les 6 derniers mois
        for (let i = months - 1; i >= 0; i--) {
            const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
            result.push({
                month: date.getMonth(),
                year: date.getFullYear(),
                name: date.toLocaleString('fr-FR', { month: 'short' }),
                count: 0,
                amount: 0
            });
        }

        // Compter les commandes pour chaque mois
        orders.forEach(order => {
            const orderDate = new Date(order.orderDate || order.createdAt);
            const monthIndex = result.findIndex(item =>
                item.month === orderDate.getMonth() &&
                item.year === orderDate.getFullYear()
            );

            if (monthIndex !== -1) {
                result[monthIndex].count++;
                result[monthIndex].amount += order.totalAmount || 0;
            }
        });

        return result;
    };

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);

                // Récupérer les produits et commandes
                let products, orders;

                
                    // Mode admin/manager - récupérer toutes les données
                    [products, orders] = await Promise.all([
                        getAllProducts(),
                        getAllOrders()
                    ]);
                

                    // === MODE STANDARD (ADMIN OU AUTRE RÔLE) ===
                    // Séparer les commandes clients (ventes) des commandes fournisseurs (approvisionnements)
                    const clientOrders = orders.filter(order => order.type === "CLIENT");
                    const supplierOrders = orders.filter(order => order.type === "FOURNISSEUR");

                    // Filtrer les produits avec un stock faible (moins de 10 unités)
                    const lowStock = products
                        .filter(product => product.stockQuantity < 10)
                        .sort((a, b) => a.stockQuantity - b.stockQuantity)
                        .slice(0, 3);

                    // Calculer le nombre de commandes CLIENTS de ce mois-ci
                    const currentDate = new Date();
                    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                    const ordersThisMonth = clientOrders.filter(order =>
                        new Date(order.orderDate || order.createdAt) >= firstDayOfMonth
                    ).length;

                    // Récupérer les clients uniques
                    const uniqueClients = new Set();
                    clientOrders.forEach(order => {
                        const clientId = order?.partner?.id || order?.client?.id;
                        if (clientId) {
                            uniqueClients.add(clientId);
                        }
                    });

                    // Récupérer les commandes clients récentes
                    const recentOrders = clientOrders
                        .sort((a, b) => new Date(b.orderDate || b.createdAt) - new Date(a.orderDate || a.createdAt))
                        .slice(0, 3)
                        .map(order => ({
                            id: order.id,
                            clientName: getClientName(order),
                            orderDate: new Date(order.orderDate || order.createdAt),
                            orderNumber: order.orderNumber || `Commande #${order.id}`,
                            totalAmount: order.totalAmount || 0
                        }));

                    // Préparer les données pour le graphique des ventes
                    const last6Months = [];
                    for (let i = 5; i >= 0; i--) {
                        const d = new Date();
                        d.setMonth(d.getMonth() - i);
                        last6Months.push({
                            name: d.toLocaleString('fr-FR', { month: 'short' }),
                            year: d.getFullYear(),
                            month: d.getMonth()
                        });
                    }

                    const salesData = last6Months.map(month => {
                        const monthOrders = clientOrders.filter(order => {
                            const orderDate = new Date(order.orderDate || order.createdAt);
                            return orderDate.getMonth() === month.month &&
                                orderDate.getFullYear() === month.year;
                        });

                        const totalSales = monthOrders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);

                        return {
                            name: month.name,
                            ventes: totalSales
                        };
                    });

                    setStats({
                        totalProducts: products.length,
                        ordersThisMonth,
                        activeClients: uniqueClients.size,
                        lowStockProducts: lowStock,
                        recentOrders,
                        salesData,
                        // Ajouter aussi les stats du distributeur pour un code plus homogène
                        totalOrders: clientOrders.length,
                        pendingOrders: clientOrders.filter(order => order.status === 'PENDING').length,
                        mostOrderedProducts: [],
                        ordersByStatus: getOrdersByStatus(clientOrders),
                        ordersByMonth: getOrdersByMonth(clientOrders)
                    });
                
            } catch (error) {
                console.error("Erreur lors de la récupération des données :", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [user]);

    if (loading) {
        return <Loading message="Chargement des données du tableau de bord..." />;
    }

    

    // Rendu pour les autres utilisateurs (admin, etc.) - Version originale
    return (
        <Container fluid>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="h3">Tableau de bord</h1>
                <div className="text-muted">Bienvenue, {user?.fullName || 'Utilisateur'}</div>
            </div>

            {/* Stats Cards */}
            <Row className="mb-4">
                <Col md={4} className="mb-3">
                <Link to="/products" style={{ textDecoration: 'none' }}>
                    <Card className="h-100 shadow-sm cursor-pointer">
                        <Card.Body className="d-flex align-items-center">
                            <div className="rounded-circle bg-primary bg-opacity-25 p-3 me-3">
                                <ShoppingBag size={24} className="text-primary" />
                            </div>
                            <div>
                                <h6 className="text-muted mb-1">Total Produits</h6>
                                <h3 className="mb-0">{stats.totalProducts}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Link>
                </Col>
                <Col md={4} className="mb-3">
                <Link to="/orders" style={{ textDecoration: 'none' }}>
                    <Card className="h-100 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                            <div className="rounded-circle bg-success bg-opacity-25 p-3 me-3">
                                <Package size={24} className="text-success" />
                            </div>
                            <div>
                                <h6 className="text-muted mb-1">Commandes du mois</h6>
                                <h3 className="mb-0">{stats.ordersThisMonth}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Link>
                </Col>
                <Col md={4} className="mb-3">
                <Link to="/clients" style={{ textDecoration: 'none' }}>
                    <Card className="h-100 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                            <div className="rounded-circle bg-info bg-opacity-25 p-3 me-3">
                                <Users size={24} className="text-info" />
                            </div>
                            <div>
                                <h6 className="text-muted mb-1">Clients actifs</h6>
                                <h3 className="mb-0">{stats.activeClients}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                    </Link>
                </Col>
            </Row>
            <Row>
                <Col lg={8} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Aperçu des ventes</h5>
                        </Card.Header>
                        <Card.Body>
                            {stats.salesData && stats.salesData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart
                                        data={stats.salesData}
                                        margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip
                                            formatter={(value) => [`${formatCurrency(value)}`, 'Ventes']}
                                        />
                                        <Bar dataKey="ventes" fill="#8884d8" />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="text-center py-5">
                                    <BarChart2 size={48} className="text-muted mb-3" />
                                    <p>Aucune donnée de vente disponible</p>
                                    <p className="text-muted">Les données apparaîtront ici une fois que des commandes seront passées</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={4}>
                    {/* <Card className="shadow-sm mb-4">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Stock faible</h5>
                        </Card.Header>
                        <Card.Body>
                            {stats.lowStockProducts && stats.lowStockProducts.length > 0 ? (
                                stats.lowStockProducts.map((product, index) => (
                                    <div key={product.id} className={`d-flex align-items-center ${index < stats.lowStockProducts.length - 1 ? 'mb-3 pb-3 border-bottom' : ''}`}>
                                        <div className="rounded-circle bg-warning bg-opacity-25 p-2 me-3">
                                            <AlertTriangle size={20} className="text-warning" />
                                        </div>
                                        <div>
                                            <h6 className="mb-0">{product.name}</h6>
                                            <small className="text-danger">{product.stockQuantity} en stock</small>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-3">
                                    <p className="mb-0 text-muted">Aucun produit en stock faible</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card> */}

                    <Card className="shadow-sm">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Commandes récentes</h5>
                        </Card.Header>
                        <Card.Body>
                            {stats.recentOrders && stats.recentOrders.length > 0 ? (
                                stats.recentOrders.map((order, index) => (
                                    <div key={order.id} className={`d-flex align-items-center ${index < stats.recentOrders.length - 1 ? 'mb-3 pb-3 border-bottom' : ''}`}>
                                        <div className="rounded-circle bg-light p-2 me-3">
                                            <Package size={20} className="text-primary" />
                                        </div>
                                        <div>
                                            <h6 className="mb-0">{order.clientName}</h6>
                                            <small className="text-muted d-block">{order.orderNumber}</small>
                                            <small className="text-muted">{getRelativeTime(order.orderDate)}</small>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-3">
                                    <p className="mb-0 text-muted">Aucune commande récente</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Dashboard;